import React, { useState } from 'react';

import CollapsibleSection from '../../design-system/CollapsibleSection';
import Typography from '../../design-system/Typography';
import Input from '../../design-system/Input';
import Textarea from '../../design-system/Textarea';
import { colors, spacing } from '../../design-system/tokens';

/**
 * Dados do Responsável — seção do fluxo de abastecimento.
 *
 * Preenchimento manual apenas (nome/e-mail/telefone + comentário). A seleção de
 * contatos cadastrados foi removida a pedido do negócio; o formulário fica
 * sempre visível e propaga cada alteração via `onChange`.
 *
 * Todos os campos são obrigatórios (o gating no hook/container valida
 * name/phone/email/details).
 */

export interface ResponsavelData {
  name: string;
  phone: string;
  email: string;
}

export interface DadosResponsavelSectionProps {
  /** Dados iniciais (re-hidratação ao reabrir o local para edição). */
  initialSelected?: ResponsavelData | null;
  /** Comentário inicial (restaura o texto ao reabrir para edição). */
  initialComment?: string;
  /** Disparado quando os dados do responsável mudam. */
  onChange?: (data: ResponsavelData | null, comment: string) => void;
  /** Destaca de vermelho os campos obrigatórios vazios (validação). @default false */
  error?: boolean;
}

const PhoneIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    width="24"
    height="24"
  >
    <path
      d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5.5 5.5l1.5-2 4 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 6a2 2 0 0 1 2-2z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/** Máscara BR de celular: "(11) 9 8765-4321". */
const maskPhone = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.replace(/^(\d*)/, '($1');
  if (d.length <= 3) return d.replace(/^(\d{2})(\d*)/, '($1) $2');
  if (d.length <= 7) return d.replace(/^(\d{2})(\d{1})(\d*)/, '($1) $2 $3');
  return d.replace(/^(\d{2})(\d{1})(\d{4})(\d*)/, '($1) $2 $3-$4');
};

const isEmailValid = (v: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const phoneDigits = (v: string): number => v.replace(/\D/g, '').length;

const FIELD_MAX_WIDTH = 440;

export const DadosResponsavelSection = ({
  initialSelected = null,
  initialComment = '',
  onChange,
  error = false,
}: DadosResponsavelSectionProps): JSX.Element => {
  const [form, setForm] = useState<ResponsavelData>({
    name: initialSelected?.name ?? '',
    email: initialSelected?.email ?? '',
    phone: initialSelected?.phone ?? '',
  });
  const [comment, setComment] = useState(initialComment);

  const setField =
    (field: keyof ResponsavelData) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const value =
        field === 'phone' ? maskPhone(e.target.value) : e.target.value;
      const next = { ...form, [field]: value };
      setForm(next);
      onChange?.(next, comment);
    };

  const handleCommentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const c = e.target.value;
    setComment(c);
    onChange?.(form, c);
  };

  const nameError = error && form.name.trim().length === 0;
  const emailError = error && !isEmailValid(form.email);
  const phoneError = error && phoneDigits(form.phone) < 10;
  const commentError = error && comment.trim().length === 0;

  return (
    <CollapsibleSection title="Dados do Responsável" defaultExpanded>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}
        >
          <Typography as="p" size="lg" weight="bold" color="#3D3D3D">
            Responsável pelo recebimento
          </Typography>
          <Typography
            as="p"
            size="sm"
            weight="regular"
            color={colors.textSecondary}
          >
            Preencha os dados do responsável pelo recebimento do abastecimento.
          </Typography>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.lg,
            maxWidth: FIELD_MAX_WIDTH,
          }}
        >
          <Input
            label="Nome completo"
            required
            placeholder="Digite"
            value={form.name}
            onChange={setField('name')}
            error={nameError}
            fullWidth
          />
          <Input
            label="E-mail"
            required
            type="email"
            placeholder="Digite"
            value={form.email}
            onChange={setField('email')}
            error={emailError}
            fullWidth
          />
          <Input
            label="Telefone"
            required
            inputMode="tel"
            placeholder="(__) _ ____-____"
            value={form.phone}
            onChange={setField('phone')}
            error={phoneError}
            trailingIcon={<PhoneIcon />}
            fullWidth
          />
          <Textarea
            label="Comentário"
            required
            placeholder="Deixe seu comentário"
            value={comment}
            onChange={handleCommentChange}
            error={commentError}
            fullWidth
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default DadosResponsavelSection;
